import { Injectable } from '@angular/core';
import { SessionService } from '@app/shared/services/session/session.service';
import { UtilService } from '@app/shared/services/utils/util.service';
import { BehaviorSubject, map, Observable, distinctUntilChanged, tap, Subject } from 'rxjs';
import { FilterResponseType, FilterType } from './filter.enums';
import {
  IFilterSelection,
  IFilter,
  IHierarchyValue,
  IChecklistValue,
  IListValue,
  IDropdownValue,
  IChipsValue,
  IDateRange,
  ICustomDate,
  IFilterResponse,
  IFilterConfig,
} from './filter.interfaces';

@Injectable({
  providedIn: 'root',
})
export class FilterService {
  public filterId: string;
  private maxWidth: number;

  private _currentFilterGroupsConfig$ = new BehaviorSubject<IFilter>(null);
  private _showFilterButton$ = new BehaviorSubject<boolean>(true);
  private _allAppliedFilters$ = new BehaviorSubject<IFilterSelection[]>([]);
  private _appliedFiltersCount$ = new BehaviorSubject<number>(0);
  public initializeToggleState$ = new Subject<void>();

  // TODO:FILTER-REFACTOR
  // remove next two properties (tag related)
  // are only to mantain old filters working
  public _tagToRemove$ = new Subject<IChipsValue>();
  public _tagRemoved$ = new Subject<boolean>();

  public _defaultValuesLoaded$ = new Subject<boolean>();

  public showFilterButton$: Observable<boolean> = this._showFilterButton$.asObservable();

  public appliedFilters$: Observable<IFilterSelection[]> = this._allAppliedFilters$.pipe(
    tap((filterSelections: IFilterSelection[]) => this.saveCacheFilters(filterSelections)),
    map((filterSelections: IFilterSelection[]) => {
      const appliedFilters = filterSelections.filter((filterValue) => filterValue.filterId === this.filterId);
      this.setFilterCount(appliedFilters);
      return appliedFilters;
    })
  );

  public currentFilterGroupsConfig$: Observable<IFilter> = this._currentFilterGroupsConfig$.pipe(
    map((filter: IFilter) => this.setFilterValues(filter))
  );

  public appliedFiltersCount$: Observable<number> = this._appliedFiltersCount$.asObservable();

  public appliedFilterTags$: Observable<IChipsValue[]> = this.appliedFilters$.pipe(
    map((filterSelections: IFilterSelection[]) => this.getTagList(filterSelections))
  );

  public filterResponse$: Observable<IFilterResponse> = this.appliedFilters$.pipe(
    map((filterSelections: IFilterSelection[]) => this.getFilterResponse(filterSelections)),
    distinctUntilChanged()
  );

  constructor(private _sessionService: SessionService) {
    this.getCacheFilters();
  }

  public setFilterConfig(filter: IFilter): void {
    this.filterId = filter.filterId;
    this._currentFilterGroupsConfig$.next(filter);
    this.setShowButton(this.maxWidth);
  }

  setFilterValues(filter: IFilter): IFilter {
    const hasCacheData =
      this._allAppliedFilters$.getValue().findIndex((filter) => filter.filterId === this.filterId) >= 0;

    if (!hasCacheData) {
      const filters = filter?.filterGroupConfigs?.flatMap((group) => group.filters);
      const appliedFilters = filters?.map((filter) => {
        const hasCustomResponse = filter.customResponse !== undefined && filter.customResponse !== null;

        return {
          filterId: this.filterId,
          type: filter.type,
          responseType: filter.responseType,
          key: filter.key,
          values: filter.values,
          hasCustomResponse,
        };
      });
      this.changeAppliedFilters(appliedFilters);
    } else {
      filter.filterGroupConfigs.forEach((group) => {
        group.filters.map((filter) => {
          const oldFilters = this._allAppliedFilters$.getValue() ?? [];

          const cacheValues = oldFilters.find(
            (oldFilter) => oldFilter.filterId === this.filterId && oldFilter.key === filter.key
          );

          filter.values = cacheValues?.values;

          return filter;
        });
      });
    }

    return filter;
  }

  public setShowButton(maxWidth: number): void {
    const WIDTH_TOLERANCE = 251;
    this.maxWidth = maxWidth;
    const isMobile = UtilService.isMobileAndTabletSize();
    const actualFilters = this._currentFilterGroupsConfig$.getValue();
    const filterList = actualFilters?.filterGroupConfigs?.flatMap((filterGroup) => filterGroup.filters);
    const customRangeCount = filterList?.filter((filter) => filter.type === FilterType.CustomDate)?.length ?? 0;
    const hasSpace = (filterList?.length + customRangeCount * 3) * WIDTH_TOLERANCE < maxWidth;
    this._showFilterButton$.next(isMobile || !hasSpace);
  }

  public changeAppliedFilters(newFilters: IFilterSelection[]): void {
    const oldFilters = this._allAppliedFilters$.getValue();
    const otherFilters = oldFilters?.filter((filter) => filter.filterId !== this.filterId);
    const allFilters = otherFilters.concat(newFilters);

    this._allAppliedFilters$.next(allFilters);
  }

  public cleanAppliedFilters(): void {
    const oldFilters = this._allAppliedFilters$.getValue();
    const newFilters = oldFilters.map((filter: IFilterSelection) => {
      if (filter.filterId === this.filterId) {
        if (filter.type === FilterType.Toggle) {
          filter.values = false;
        } else {
          filter.values = null;
        }
      }

      return filter;
    });
    this._allAppliedFilters$.next(newFilters);
  }

  private getFilterResponse(filterSelections: IFilterSelection[]): IFilterResponse {
    const filters = this.getFiltersApplied(filterSelections);

    const filter = filters.filter((selection) => selection.responseType === FilterResponseType.Filter);

    const selections = filters.filter((selection) => selection.responseType === FilterResponseType.Selections);

    const gridFilters = filters.filter((selection) => selection.responseType === FilterResponseType.GridFilters);

    const reortFilters = filters.filter((selection) => selection.responseType === FilterResponseType.ReportFilters);

    const filterResponse: IFilterResponse = {
      filter: this.getFilterString(filter) ?? '',
      selections: this.getSelectionsString(selections) ?? '',
      gridFilters: this.getFilterGridList(gridFilters) ?? null,
      reportFilters: this.getFilterReportString(reortFilters) ?? '',
    };

    return filterResponse;
  }

  // TODO:FILTER-REFACTOR
  // remove next two methods(tag related)
  // are only to mantain old filters working

  public removeTagFilter(tag: IChipsValue): void {
    this._tagToRemove$.next(tag);
  }

  public tagWasRemoved(removed: boolean): void {
    this._tagRemoved$.next(removed);
  }

  private getFiltersApplied(filterSelections: IFilterSelection[]): IFilterSelection[] {
    const filters = filterSelections.filter(
      (filter) =>
        filter.filterId === this.filterId &&
        filter.values !== undefined &&
        filter.values !== null &&
        (!Array.isArray(filter.values) || (Array.isArray(filter.values) && filter.values.length > 0))
    );

    return filters;
  }

  private setFilterCount(filterSelections: IFilterSelection[]): void {
    const filtersCount = this.getFiltersAppliedCount(filterSelections);
    this._appliedFiltersCount$.next(filtersCount);
  }

  private getFiltersAppliedCount(filterSelections: IFilterSelection[]): number {
    const filterFunction = (filter: IFilterSelection) => {
      if (filter.filterId !== this.filterId) return false;
      const values = filter.values as any;
      const isDefined = !!(values ?? false);
      if (!isDefined) return false;
      const isArray = Array.isArray(values);
      return isArray ? values.length > 0 : Boolean(values);
    };

    return filterSelections.filter((filter) => filterFunction(filter)).length;
  }

  private getSelectionsString(filterSelections: IFilterSelection[]): string {
    return this.buildSelectionsString(filterSelections);
  }

  private getFilterString(filterSelections: IFilterSelection[]): string {
    return this.buildFilterString(filterSelections);
  }

  private getFilterGridList(filterSelections: IFilterSelection[]): string[] {
    return this.buildFilterGridListString(filterSelections);
  }

  private getFilterReportString(filterSelection: IFilterSelection[]): string {
    return this.buildFilterReportString(filterSelection);
  }

  private buildSelectionsString(filterSelections: IFilterSelection[]): string {
    let selectionsString = '';

    if (filterSelections.length === 0) {
      return selectionsString;
    }

    selectionsString = '{"';
    filterSelections.forEach((filter, index) => {
      if (filter.hasCustomResponse) {
        const filterConfig = this.getFilterConfig(filter.key);
        selectionsString = selectionsString + filterConfig.customResponse(filter);
      } else {
        selectionsString = selectionsString + filter.key + '":"';
        if (Array.isArray(filter.values)) {
          const values = filter.values;
          values.forEach((filterValue, index) => {
            selectionsString = selectionsString + this.strategyMap(filter, filterValue);
            if (index !== values.length - 1) {
              selectionsString = selectionsString + ',';
            }
          });
        } else {
          selectionsString = selectionsString + this.strategyMap(filter, filter.values);
        }
      }
      if (index !== filterSelections.length - 1) {
        selectionsString = selectionsString + '","';
      }
    });
    selectionsString = selectionsString + '"}';

    return selectionsString;
  }

  private buildFilterString(filterSelections: IFilterSelection[]): string {
    let filterString = '';

    if (filterSelections.length === 0) {
      return filterString;
    }

    filterString = '';
    filterSelections.forEach((filter, index) => {
      if (filter.hasCustomResponse) {
        const filterConfig = this.getFilterConfig(filter.key);
        filterString = filterString + filterConfig.customResponse(filter);
      } else {
        filterString = filterString + "'" + filter.key + "':";
        if (Array.isArray(filter.values)) {
          const values = filter.values;
          values.forEach((filterValue, index) => {
            filterString = filterString + this.strategyMap(filter, filterValue);
            if (index !== values.length - 1) {
              filterString = filterString + ',';
            }
          });
        } else {
          filterString = filterString + this.strategyMap(filter, filter.values);
        }
      }
      if (index !== filterSelections.length - 1) {
        filterString = filterString + ',';
      }
    });

    return filterString;
  }

  private buildFilterGridListString(filterSelections: IFilterSelection[]): string[] {
    const filterList = [];

    if (filterSelections.length === 0) {
      return null;
    }

    filterSelections.forEach((filter, index) => {
      if (filter.hasCustomResponse) {
        const filterConfig = this.getFilterConfig(filter.key);
        filterList.push(filterConfig.customResponse(filter));
      } else {
        if (Array.isArray(filter.values)) {
          const values = filter.values;
          values.forEach((filterValue, index) => {
            filterList.push([`${filter.key}`, '=', `${this.strategyMap(filter, filterValue)}`]);
            if (index !== values.length - 1) {
              filterList.push('or');
            }
          });
        } else {
          filterList.push([`${filter.key}`, '=', `${this.strategyMap(filter, filter.values)}`]);
        }
      }
      if (index !== filterSelections.length - 1) {
        filterList.push('and');
      }
    });

    return filterList;
  }

  private buildFilterReportString(filterSelections: IFilterSelection[]): string {
    let filterString = '';

    if (filterSelections.length === 0) {
      return filterString;
    }

    filterString = '';
    filterSelections.forEach((filter, index) => {
      if (filter.hasCustomResponse) {
        const filterConfig = this.getFilterConfig(filter.key);
        filterString = filterString + filterConfig.customResponse(filter);
      } else {
        filterString = filterString + filter.key + '=';
        if (Array.isArray(filter.values)) {
          const values = filter.values;
          values.forEach((filterValue, index) => {
            filterString = filterString + this.strategyMap(filter, filterValue);
            if (index !== values.length - 1) {
              filterString = filterString + ',';
            }
          });
        } else {
          filterString = filterString + this.strategyMap(filter, filter.values);
        }
      }
      if (index !== filterSelections.length - 1) {
        filterString = filterString + '&';
      }
    });

    return filterString;
  }

  private strategyMap(filter: IFilterSelection, value: any): string {
    const { type, hasNestedKey, key } = filter;
    value = hasNestedKey ? this.getNestedObject(value, key) : value;
    switch (type) {
      case FilterType.Hierarchy:
        return this.hierarchyValue(value);
      case FilterType.Checklist:
        return this.checklistValue(value);
      case FilterType.List:
        return this.listValue(value);
      case FilterType.Dropdown:
        return this.dropdownValue(value);
      case FilterType.Chips:
        return this.chipsValue(value);
      case FilterType.DateRange:
        return this.dateRange(value);
      case FilterType.CustomDate:
        return this.customDate(value);
      default:
        return ' ';
    }
  }

  private hierarchyValue(filterValue: IHierarchyValue): string {
    return filterValue._id;
  }

  private checklistValue(filterValue: IChecklistValue): string {
    return filterValue.value._id;
  }

  private listValue(filterValue: IListValue): string {
    return filterValue._id;
  }

  private dropdownValue(filterValue: IDropdownValue): string {
    return filterValue.value;
  }

  private chipsValue(filterValue: IChipsValue): string {
    return filterValue._id;
  }

  private dateRange(filterValue: IDateRange): string {
    const prefix = filterValue.name === 'startDate' ? '{$gte:ISODate' : '$lt:ISODate';
    const sufix = filterValue.name === 'endDate' ? '}' : '';

    return `${prefix}('${filterValue._id}')${sufix}`;
  }

  private customDate(filterValue: ICustomDate): string {
    if (filterValue.value.dateFilter === 'customRange') {
      const customStartDate = new Date(filterValue.value.startDate).toISOString().substring(0, 10);
      const customEndDate = new Date(filterValue.value.endDate).toISOString().substring(0, 10);
      const customStartDateString = `date,isGreaterThanOrEqual,#=${customStartDate}`;
      const customEndDateString = `&date,isLessThanOrEqual,#=${customEndDate}`;
      return `${customStartDateString + customEndDateString}`;
    } else {
      const defaultRange = filterValue.value.dateFilter;
      const defaultRangeString = `defaultrange=${defaultRange}`;
      return `${defaultRangeString}`;
    }
  }

  private getTagList(filterSelections: IFilterSelection[]): IChipsValue[] {
    const chipFilters = filterSelections.filter((filter) => filter.type === FilterType.Chips);

    let tagList = [];
    chipFilters.forEach((filter) => {
      const tags = Array.isArray(filter.values)
        ? filter.values.map((value) => {
            return { ...value, key: filter.key };
          })
        : [];
      tagList = tagList.concat(tags);
    });

    return tagList;
  }

  private getFilterConfig(filterKey: string): IFilterConfig {
    const filterGroupConfigs = this._currentFilterGroupsConfig$.getValue();
    const filterConfigs = filterGroupConfigs?.filterGroupConfigs.flatMap((group) => group.filters);
    return filterConfigs.find((config) => config.key === filterKey);
  }

  public getShowFilterButton(): boolean {
    return this._showFilterButton$.getValue();
  }

  private getCacheFilters(): void {
    const appliedFilters = this._sessionService.getItem('CM_CACHE_FILTERS') ?? [];
    this._allAppliedFilters$.next(appliedFilters);
  }

  private saveCacheFilters(filterRespopnse: IFilterSelection[]): void {
    this._sessionService.setItem('CM_CACHE_FILTERS', filterRespopnse);
  }

  private getNestedObject(rootObject: Object, key: string) {
    return key
      .split('.')
      .slice(0, -1)
      .reduce((obj, k) => obj && obj[k], rootObject);
  }
}
